import { useState, useEffect } from 'react';
import { API_URL } from '../config';
import { apiFetch } from '../utils/api';
import { Plus, Search, Trash2, Edit2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function PromotionsPage() {
    const { user } = useAuth();
    const canEdit = Array.isArray(user?.permissions) && user.permissions.includes('promociones_editar');
    const canDelete = Array.isArray(user?.permissions) && user.permissions.includes('promociones_eliminar');
    const [promotions, setPromotions] = useState([]);
    const [products, setProducts] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);

    // Form
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        monto: '', // Price of the pack/promo
        items: []
    });

    // Helper for adding items to promo
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchPromotions();
        fetchProducts();
    }, []);

    const fetchPromotions = async () => {
        try {
            const res = await apiFetch(`${API_URL}/api/promotions`);
            if (res.ok) setPromotions(await res.json());
        } catch (e) { console.error(e); }
    };

    const fetchProducts = async () => {
        try {
            // Fetch all products without limit
            let allProducts = [];
            let page = 1;
            let hasMore = true;
            
            while (hasMore) {
                const res = await apiFetch(`${API_URL}/api/products?limit=1000&offset=${(page - 1) * 1000}`);
                if (res.ok) {
                    const data = await res.json();
                    allProducts = [...allProducts, ...data];
                    hasMore = data.length === 1000;
                    page++;
                } else {
                    hasMore = false;
                }
            }
            
            setProducts(allProducts);
        } catch (e) { 
            console.error(e);
            // Fallback to single request
            try {
                const res = await apiFetch(`${API_URL}/api/products`);
                if (res.ok) setProducts(await res.json());
            } catch (e2) { console.error(e2); }
        }
    };

    const addToPromo = (product) => {
        setFormData(prev => {
            if (prev.items.find(i => i.id === product.id)) return prev;
            return {
                ...prev,
                items: [...prev.items, { ...product, quantity: 1 }]
            };
        });
    };

    const updateItemQty = (id, newQty) => {
        const q = Math.max(1, parseInt(newQty, 10) || 1);
        setFormData(prev => ({
            ...prev,
            items: prev.items.map(i => i.id === id ? { ...i, quantity: q } : i)
        }));
    };

    const removeItem = (id) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter(i => i.id !== id)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingPromo
                ? `${API_URL}/api/promotions/${editingPromo.id}`
                : `${API_URL}/api/promotions`;
            const method = editingPromo ? 'PUT' : 'POST';
            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            if (res.ok) {
                alert(editingPromo ? 'Promoción actualizada con éxito' : 'Promoción creada con éxito');
                setShowModal(false);
                setEditingPromo(null);
                setFormData({ nombre: '', descripcion: '', monto: '', items: [] });
                fetchPromotions();
            } else {
                const err = await res.json();
                alert(err.error || 'Error al guardar promoción');
            }
        } catch (err) { console.error(err); }
    };

    const handleEdit = async (promo) => {
        try {
            const res = await apiFetch(`${API_URL}/api/promotions/${promo.id}`);
            if (!res.ok) return;
            const data = await res.json();
            setFormData({
                nombre: data.nombre || '',
                descripcion: data.descripcion || '',
                monto: String(data.monto || ''),
                items: (data.items || []).map(i => ({ id: i.producto_id, nombre: i.nombre, valor_producto: i.valor_producto, quantity: i.cantidad }))
            });
            setEditingPromo(promo);
            setShowModal(true);
        } catch (e) { console.error(e); }
    };

    const handleDelete = async (promo) => {
        if (!confirm(`¿Eliminar la promoción "${promo.nombre}"?`)) return;
        try {
            const res = await apiFetch(`${API_URL}/api/promotions/${promo.id}`, { method: 'DELETE' });
            if (res.ok) {
                fetchPromotions();
            } else {
                const err = await res.json();
                alert(err.error || 'Error al eliminar promoción');
            }
        } catch (e) { console.error(e); }
    };

    const filteredProducts = products.filter(p =>
        p.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Formato chileno: separador de miles con punto (3.000, 32.760)
    const formatCurrency = (value) => {
        const num = parseFloat(value) || 0;
        return new Intl.NumberFormat('es-CL', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(num);
    };

    return (
        <div className="p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Promociones y Packs</h1>
                {canEdit && (
                    <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
                        <Plus size={18} /> Nueva Promoción
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {promotions.map(promo => (
                    <div key={promo.id} className="bg-white p-6 rounded-lg shadow border hover:shadow-md transition">
                        <div className="flex justify-between items-start mb-2">
                            <h3 className="font-bold text-lg">{promo.nombre}</h3>
                            <div className="flex items-center gap-2">
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">Activa</span>
                                {canEdit && (
                                    <button
                                        onClick={() => handleEdit(promo)}
                                        className="p-1.5 rounded hover:bg-indigo-50 text-indigo-600"
                                        title="Editar"
                                    >
                                        <Edit2 size={16} />
                                    </button>
                                )}
                                {canDelete && (
                                    <button
                                        onClick={() => handleDelete(promo)}
                                        className="p-1.5 rounded hover:bg-red-50 text-red-600"
                                        title="Eliminar"
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm mb-4">{promo.descripcion}</p>
                        <div className="border-t pt-4 flex justify-between items-center">
                            <span className="text-gray-500 text-sm">Precio Promo:</span>
                            <span className="text-xl font-bold text-indigo-600">${formatCurrency(promo.monto)}</span>
                        </div>
                    </div>
                ))}
            </div>

            {showModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto flex flex-col md:flex-row">
                        {/* Form Side */}
                        <div className="p-6 md:w-1/2 border-r">
                            <h2 className="modal-title">{editingPromo ? 'Editar Promoción' : 'Crear Pack / Promoción'}</h2>
                            <form onSubmit={handleSubmit} id="promoForm" className="flex flex-col gap-4">
                                <div className="form-group full-width">
                                    <label>Nombre Promoción</label>
                                    <input required
                                        value={formData.nombre} onChange={e => setFormData({ ...formData, nombre: e.target.value })} />
                                </div>
                                <div className="form-group full-width">
                                    <label>Descripción</label>
                                    <textarea className="w-full border rounded p-2" rows="2"
                                        value={formData.descripcion} onChange={e => setFormData({ ...formData, descripcion: e.target.value })}></textarea>
                                </div>
                                <div className="form-group full-width">
                                    <label>Precio Final del Pack</label>
                                    <input required type="number" className="font-bold text-lg"
                                        value={formData.monto} onChange={e => setFormData({ ...formData, monto: e.target.value })} />
                                </div>

                                <h3 className="font-bold mt-6 mb-2">Productos en el Pack</h3>
                                {formData.items.length === 0 ? (
                                    <p className="text-gray-400 text-sm italic">Seleccione productos de la derecha...</p>
                                ) : (
                                    <div className="space-y-2">
                                        {formData.items.map(item => (
                                            <div key={item.id} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                                                <div className="text-sm px-1">
                                                    <div className="font-medium">{item.nombre}</div>
                                                    <div className="text-xs text-gray-500">${formatCurrency(item.valor_producto)} Unit.</div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="w-24 min-h-[3rem] min-w-[5rem] border-2 border-indigo-200 rounded-xl px-3 py-3 text-center text-xl font-bold text-gray-900 shadow-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-500"
                                                        value={item.quantity}
                                                        onChange={e => updateItemQty(item.id, e.target.value)}
                                                    />
                                                    <button type="button" onClick={() => removeItem(item.id)} className="text-red-500"><Trash2 size={16} /></button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <div className="mt-6 flex justify-end gap-2">
                                    <button type="button" onClick={() => { setShowModal(false); setEditingPromo(null); setFormData({ nombre: '', descripcion: '', monto: '', items: [] }); }} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancelar</button>
                                    <button type="submit" className="btn-primary">{editingPromo ? 'Actualizar Promoción' : 'Guardar Promoción'}</button>
                                </div>
                            </form>
                        </div>

                        {/* Search Side */}
                        <div className="p-6 md:w-1/2 bg-gray-50">
                            <h3 className="font-bold mb-4">Agregar Productos</h3>
                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-3 text-gray-400 pointer-events-none" size={18} />
                                <input type="text" placeholder="Buscar..." className="w-full border pl-14 p-2 rounded"
                                    value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                            </div>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {filteredProducts.map(p => (
                                    <button key={p.id} type="button" onClick={() => addToPromo(p)}
                                        className="w-full text-left p-3 bg-white border rounded hover:shadow-sm flex justify-between items-center group">
                                        <div>
                                            <div className="font-medium">{p.nombre}</div>
                                            <div className="text-xs text-gray-500">{p.codigo_barra}</div>
                                        </div>
                                        <Plus size={18} className="text-indigo-600 opacity-0 group-hover:opacity-100" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
